import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  Plus, Search, Edit2, Trash2, Newspaper, Users,
  Check, X
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCirculation = (count) => {
  if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
  if (count >= 1000) return `${(count / 1000).toFixed(0)}K`;
  return count;
};

const AdminPrintMedia = () => {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('');
  const [deleteModal, setDeleteModal] = useState({ open: false, item: null });

  useEffect(() => {
    fetchMedia();
  }, []);

  const fetchMedia = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/print-media`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMedia(response.data);
    } catch (error) {
      console.error('Error fetching print media:', error);
      toast.error('Failed to load print media');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteModal.item) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/print-media/${deleteModal.item.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Print media deleted');
      setMedia(media.filter(m => m.id !== deleteModal.item.id));
      setDeleteModal({ open: false, item: null });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const toggleActive = async (item) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/print-media/${item.id}`, 
        { is_active: !item.is_active },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setMedia(media.map(m => 
        m.id === item.id ? { ...m, is_active: !m.is_active } : m
      ));
      toast.success(`Media ${item.is_active ? 'deactivated' : 'activated'}`);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const filteredMedia = media.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = !filterType || item.media_type === filterType;
    return matchesSearch && matchesType;
  });

  const mediaTypes = ['magazine', 'newspaper', 'journal'];

  return (
    <div className="p-6" data-testid="admin-print-media">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#1a1a1a]">Print Media</h1>
          <p className="text-sm text-gray-500">Manage magazines, newspapers, and print ad placements</p>
        </div>
        <Link
          to="/admin/print-media/new"
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-white rounded-lg hover:bg-[#B8960F] transition-colors"
          data-testid="add-print-media-btn"
        >
          <Plus className="w-4 h-4" />
          Add Print Media
        </Link>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37]"
          />
        </div>
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37] bg-white"
        >
          <option value="">All Types</option>
          {mediaTypes.map(t => (
            <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
          ))}
        </select>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white border border-gray-100 rounded-lg p-4">
          <p className="text-2xl font-bold text-[#1a1a1a]">{media.length}</p>
          <p className="text-xs text-gray-500">Total Publications</p>
        </div>
        <div className="bg-white border border-gray-100 rounded-lg p-4">
          <p className="text-2xl font-bold text-[#1a1a1a]">{media.filter(m => m.media_type === 'magazine').length}</p>
          <p className="text-xs text-gray-500">Magazines</p>
        </div>
        <div className="bg-white border border-gray-100 rounded-lg p-4">
          <p className="text-2xl font-bold text-[#1a1a1a]">{media.filter(m => m.media_type === 'newspaper').length}</p>
          <p className="text-xs text-gray-500">Newspapers</p>
        </div>
        <div className="bg-white border border-gray-100 rounded-lg p-4">
          <p className="text-2xl font-bold text-[#1a1a1a]">{media.reduce((acc, m) => acc + (m.packages?.length || 0), 0)}</p>
          <p className="text-xs text-gray-500">Total Packages</p>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-100 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full mx-auto"></div>
          </div>
        ) : filteredMedia.length === 0 ? (
          <div className="p-8 text-center">
            <Newspaper className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-[#1a1a1a] mb-2">No Print Media</h3>
            <p className="text-gray-500 mb-4">Start by adding your first magazine or newspaper</p>
            <Link
              to="/admin/print-media/new"
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-white rounded-lg"
            >
              <Plus className="w-4 h-4" />
              Add Print Media
            </Link>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Publication</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Type</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Circulation</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Frequency</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Packages</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredMedia.map(item => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {item.logo ? (
                        <img src={item.logo} alt={item.name} className="w-10 h-10 rounded object-cover" />
                      ) : (
                        <div className="w-10 h-10 rounded bg-amber-100 flex items-center justify-center">
                          <Newspaper className="w-5 h-5 text-amber-600" />
                        </div>
                      )}
                      <div>
                        <p className="font-semibold text-[#1a1a1a]">{item.name}</p>
                        <p className="text-sm text-gray-500">{item.language}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-block px-2 py-1 rounded text-xs font-medium bg-amber-100 text-amber-700 capitalize">
                      {item.media_type}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <Users className="w-4 h-4 text-gray-400" />
                      <span className="font-semibold">{formatCirculation(item.circulation)}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-600 capitalize">{item.frequency?.replace(/_/g, ' ')}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm font-medium">{item.packages?.length || 0}</span>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => toggleActive(item)}
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                        item.is_active 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-gray-100 text-gray-500'
                      }`}
                    >
                      {item.is_active ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                      {item.is_active ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <Link
                        to={`/admin/print-media/${item.id}/edit`}
                        className="p-2 text-gray-400 hover:text-[#D4AF37] transition-colors"
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4" />
                      </Link>
                      <button
                        onClick={() => setDeleteModal({ open: true, item })}
                        className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Delete Modal */}
      {deleteModal.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-bold text-[#1a1a1a] mb-2">Delete Print Media</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete <strong>{deleteModal.item?.name}</strong>? This will also delete all associated packages.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteModal({ open: false, item: null })}
                className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPrintMedia;
